import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Textarea } from '@/components/ui/textarea';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';

import { useApprovalsStore } from '../../../store/approvals.store';
import { ApprovalsTableMeta } from '../../../types/approvals-table.types';
import { ApprovalTimeEntry, TimeEntryStatus } from '../../../types/time-tracking-approvals';

/** Auto-grow the notes textarea up to this many lines, then scroll. */
const NOTES_MAX_LINES = 4;
const NOTES_MAX_LENGTH = 1000;

interface EditableApprovalNotesCellProps {
  entry: ApprovalTimeEntry;
  meta: ApprovalsTableMeta | undefined;
}

/**
 * Inline editable approval-notes cell.
 *
 * A multiline textarea that starts as a single line and auto-grows with the
 * content up to {@link NOTES_MAX_LINES} lines; beyond that it scrolls. The field
 * fills the column width so widening the column widens the input.
 */
function EditableApprovalNotesCellComponent({ entry, meta }: EditableApprovalNotesCellProps) {
  const isApproved = entry.status === TimeEntryStatus.APPROVED;
  // From the store (keyed by entry id) so the edit survives remount/re-derivation. An
  // APPROVED entry is read-only and server-owned: never let a leftover pending edit mask
  // the notes that were actually saved.
  const pendingEdit = useApprovalsStore(s => s.editedEntryValues[entry.id]);
  const edited = isApproved ? undefined : pendingEdit;
  const notes =
    edited?.approvalNotes !== undefined
      ? (edited.approvalNotes ?? '')
      : (entry.approvalNotes ?? '');
  const [value, setValue] = useState(notes);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setValue(notes);
  }, [notes]);

  // Resize to fit content, clamped to NOTES_MAX_LINES; scroll past the cap.
  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const verticalPadding = el.offsetHeight - el.clientHeight + 8;
    const maxHeight = lineHeight * NOTES_MAX_LINES + verticalPadding;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  const handleCommit = useCallback(() => {
    // Approved entries are read-only — never commit (mirrors the old disabled behavior).
    if (isApproved) return;
    if (value !== notes) {
      meta?.onApprovalNotesChange(entry, value || null);
    }
  }, [isApproved, value, notes, entry, meta]);

  // Approved entries use `readOnly` (not `disabled`): the shared Textarea sets
  // `disabled:pointer-events-none`, which would block scrolling overflowing notes. A
  // read-only textarea is non-editable but stays focusable and scrollable; we re-apply the
  // muted "disabled" look manually since it no longer comes from the `disabled:` variants.
  return (
    <Textarea
      ref={textareaRef}
      rows={1}
      className={cn(
        'min-h-0 w-full resize-none py-1 text-sm',
        isApproved && 'cursor-default bg-muted'
      )}
      value={value}
      maxLength={NOTES_MAX_LENGTH}
      readOnly={isApproved}
      placeholder={i18n.t('authenticated.timeTracking:approvals.columns.notesPlaceholder')}
      onChange={e => setValue(e.target.value)}
      onBlur={handleCommit}
      onClick={e => e.stopPropagation()}
    />
  );
}

/**
 * Memoized: holds local input state and measures layout in an effect, so it must not
 * re-render when an unrelated row's selection changes. Props are kept stable by the table.
 */
export const EditableApprovalNotesCell = memo(EditableApprovalNotesCellComponent);
